import type { User } from "../config";
import {
  getNormalizedPermissions,
  hasPermission,
  canViewModule,
} from "../types/collaboratorCargo";
import type { NormalizedCargoPermissions } from "../types/collaboratorCargo";
import {
  COLLABORATOR_PERMISSION_MODULES,
  type PermissionModuleId,
  type PermissionActionId,
} from "../types/collaboratorPermissionRegistry";
import { isDelivaiDevSession } from "./delivaiInternal";
import { resolveInternalDevNormalizedPermissions } from "./delivaiDevCargo";
import {
  DELIVAI_CONTROL_NAV_ITEMS,
  filterDelivaiControlNavItems,
  isDelivaiControlNavActive,
} from "../pages/delivai/delivaiControlNavItems";

/** `null` = dono da loja (sem restrição de colaborador). */
export function getSessionNormalizedPermissions(
  user: User | null
): NormalizedCargoPermissions | null {
  if (!user?.isCollaborator || !user.cargoJson) {
    return null;
  }
  if (isDelivaiDevSession(user)) {
    return resolveInternalDevNormalizedPermissions(user.cargoJson);
  }
  return getNormalizedPermissions(user.cargoJson);
}

export function can(
  user: User | null,
  moduleId: PermissionModuleId,
  actionId: PermissionActionId
): boolean {
  const n = getSessionNormalizedPermissions(user);
  if (n === null) return true;
  return hasPermission(n, moduleId, actionId);
}

/** Rota `/dashboard/:segment/...` — só dono da loja. Colaboradores usam `/colaborador` ou `/dev`. */
export function canAccessRoute(user: User | null, pathname: string): boolean {
  if (user?.isCollaborator) {
    if (isDelivaiDevSession(user)) {
      return pathname.startsWith("/dev");
    }
    return false;
  }

  const seg =
    pathname.replace(/^\/dashboard\/?/, "").split("/").filter(Boolean)[0] || "";
  if (!seg) return true;
  if (seg === "delivai") return false;
  const mods = COLLABORATOR_PERMISSION_MODULES.filter((m) => m.routeSegment === seg);
  if (mods.length === 0) return true;
  return true;
}

export const COLLABORATOR_NO_ACCESS_PATH = "/colaborador/sem-acesso";

export function hasAnyCollaboratorWorkspaceAccess(user: User | null): boolean {
  if (!user?.isCollaborator || isDelivaiDevSession(user)) return false;
  return (
    canAccessCollaboratorRoute(user, "/colaborador/agenda") ||
    canAccessCollaboratorRoute(user, "/colaborador/entregas") ||
    canAccessCollaboratorRoute(user, "/colaborador/pedidos") ||
    canAccessCollaboratorRoute(user, "/colaborador/delivery")
  );
}

/** Rotas do espaço `/colaborador/*`. */
export function canAccessCollaboratorRoute(user: User | null, pathname: string): boolean {
  if (!user?.isCollaborator || isDelivaiDevSession(user)) return false;

  const seg =
    pathname.replace(/^\/colaborador\/?/, "").split("/").filter(Boolean)[0] || "";
  const n = getSessionNormalizedPermissions(user);

  if (seg === "sem-acesso") {
    return !hasAnyCollaboratorWorkspaceAccess(user);
  }

  if (seg === "agenda") {
    return !!user.agendaEnabled;
  }

  if (!seg || seg === "entregas") {
    return (
      isDelivererCargoTitulo(user.cargoJson?.titulo) ||
      (!!n && canViewModule(n, "motoboys"))
    );
  }
  if (seg === "pedidos") return !!n && canViewModule(n, "pdv");
  if (seg === "delivery") {
    if (isDelivererCargoTitulo(user.cargoJson?.titulo)) return false;
    return !!n && canViewModule(n, "delivery");
  }
  if (seg === "internal-order") {
    return (
      !!n &&
      (hasPermission(n, "pdv", "pedido_interno") ||
        hasPermission(n, "delivery", "pedido_interno"))
    );
  }
  return false;
}

/** Cargos antigos: novas ações herdam permissões já concedidas antes do registro granular. */
const DELIVAI_CONTROL_LEGACY_GRANTS: Partial<
  Record<PermissionActionId, PermissionActionId[]>
> = {
  editar_contratos: ["editar_permissoes", "editar"],
  /** Master (quem gerencia permissões / cargo Dev completo) publica e exclui versões. */
  publicar_contratos: ["editar_permissoes"],
  excluir_versoes_contratos: ["editar_permissoes"],
  editar_prompts: ["editar_cardapio"],
  editar_consumers: ["editar_cardapio"],
  ver_banco: ["ver"],
  ver_logs: ["ver"],
  ver_observabilidade: ["ver"],
};

function delivaiControlActionGranted(
  norm: NormalizedCargoPermissions,
  actionId: PermissionActionId
): boolean {
  if (!canViewModule(norm, "delivai_controle")) {
    return false;
  }
  if (hasPermission(norm, "delivai_controle", actionId)) {
    return true;
  }
  const fallbacks = DELIVAI_CONTROL_LEGACY_GRANTS[actionId];
  if (!fallbacks?.length) {
    return false;
  }
  return fallbacks.some((id) => hasPermission(norm, "delivai_controle", id));
}

/** Primeira rota do painel /dev permitida pelo cargo (null = sem acesso). */
export function getFirstAllowedDevPath(user: User | null): string | null {
  if (!isDelivaiDevSession(user)) return null;
  const items = filterDelivaiControlNavItems(DELIVAI_CONTROL_NAV_ITEMS, (actionId) =>
    canDelivaiControlAction(user, actionId)
  );
  return items[0]?.to ?? null;
}

/** Rota /dev/* permitida para o cargo atual. */
export function canAccessDevPath(user: User | null, pathname: string): boolean {
  if (!isDelivaiDevSession(user)) return false;
  const item = DELIVAI_CONTROL_NAV_ITEMS.find((nav) => isDelivaiControlNavActive(pathname, nav));
  if (!item) return false;
  if (item.end) return canDelivaiControlAction(user, "ver");
  if (!item.devAction) return canDelivaiControlAction(user, "ver");
  return canDelivaiControlAction(user, item.devAction);
}

export function canDelivaiControlAction(
  user: User | null,
  actionId: PermissionActionId
): boolean {
  if (!isDelivaiDevSession(user)) return false;
  const n = getSessionNormalizedPermissions(user);
  if (!n) return true;
  return delivaiControlActionGranted(n, actionId);
}

export function effectiveDelivaiControlActionForNorm(
  norm: NormalizedCargoPermissions,
  actionId: PermissionActionId
): boolean {
  return delivaiControlActionGranted(norm, actionId);
}

const COLLABORATOR_POST_LOGIN_CANDIDATES = [
  "/colaborador/agenda",
  "/colaborador/pedidos",
  "/colaborador/delivery",
  "/colaborador/entregas",
];

/** Mesma tela no painel da loja ou no espaço do colaborador. */
export function panelPath(
  user: User | null,
  dashboardPath: string
): string {
  if (!user?.isCollaborator || isDelivaiDevSession(user)) {
    return dashboardPath;
  }
  const qIndex = dashboardPath.indexOf("?");
  const path = qIndex >= 0 ? dashboardPath.slice(0, qIndex) : dashboardPath;
  const suffix = qIndex >= 0 ? dashboardPath.slice(qIndex) : "";
  const map: Record<string, string> = {
    "/dashboard/orders": "/colaborador/pedidos",
    "/dashboard": "/colaborador/pedidos",
    "/dashboard/delivery": "/colaborador/delivery",
    "/dashboard/internal-order": "/colaborador/internal-order",
  };
  return `${map[path] || path.replace(/^\/dashboard/, "/colaborador")}${suffix}`;
}

export function getPostLoginPath(user: User | null): string {
  if (!user?.isCollaborator) {
    return "/dashboard/delivery";
  }
  if (isDelivaiDevSession(user)) {
    return getFirstAllowedDevPath(user) ?? "/dev";
  }
  if (isDelivererCargoTitulo(user.cargoJson?.titulo)) {
    return "/colaborador/entregas";
  }
  if (canAccessCollaboratorRoute(user, "/colaborador/agenda")) {
    return "/colaborador/agenda";
  }
  for (const path of COLLABORATOR_POST_LOGIN_CANDIDATES) {
    if (canAccessCollaboratorRoute(user, path)) {
      return path;
    }
  }
  return COLLABORATOR_NO_ACCESS_PATH;
}

export function isDelivererCargoTitulo(titulo: string | undefined | null): boolean {
  const t = (titulo || "").trim().toLowerCase();
  return t === "entregador" || t === "motoboy";
}

export function orderMatchesEntregadorFilter(
  order: { entregador?: string },
  collaboratorName: string | undefined
): boolean {
  const a = ((order as any).entregador || "").trim().toLowerCase();
  const b = (collaboratorName || "").trim().toLowerCase();
  return !a || !b ? false : a === b;
}
